// Browse view for the Discover screen. An alternative to the swipe deck:
// shows every persona in a filterable list so users with a specific practice
// goal (e.g. "HSK 2, food, family-flavored") can pick deliberately instead
// of being served whatever the deck shuffler hands them next.
//
// Filtering is AND across categories, OR within a category. Filter state is
// hoisted to DiscoverScreen so it survives Swipe ↔ Browse toggles in-session.

import { useMemo } from 'react';
import type { HskLevel, Persona, ProfessionTag, TopicTag } from '@/lib/types';
import { Portrait } from '@/components/portraits';
import { FilterChip } from '@/components/ui/FilterChip';
import { C } from '@/styles/tokens';

export interface BrowseFilterState {
  hsk: ReadonlySet<HskLevel>;
  prof: ReadonlySet<ProfessionTag>;
  topic: ReadonlySet<TopicTag>;
}

export const EMPTY_BROWSE_FILTERS: BrowseFilterState = {
  hsk: new Set<HskLevel>(),
  prof: new Set<ProfessionTag>(),
  topic: new Set<TopicTag>(),
};

interface BrowseViewProps {
  personas: readonly Persona[];
  filters: BrowseFilterState;
  onFiltersChange: (next: BrowseFilterState) => void;
  onOpenProfile: (persona: Persona) => void;
}

const HSK_LEVELS: readonly HskLevel[] = [1, 2, 3, 4, 5];

// Fixed canonical order for the topic chips. Listed roughly from concrete to
// abstract so the row reads naturally left-to-right.
const TOPIC_TAGS: readonly TopicTag[] = [
  'Everyday life',
  'Food & drink',
  'Work & career',
  'Family & health',
  'Culture & history',
  'Books & ideas',
  'Arts & crafts',
  'Nature & outdoors',
];

function toggle<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

function uniqueProfs(personas: readonly Persona[]): ProfessionTag[] {
  const seen = new Set<ProfessionTag>();
  for (const p of personas) {
    if (p.professionTag) seen.add(p.professionTag);
  }
  return Array.from(seen).sort();
}

function usedTopicTags(personas: readonly Persona[]): TopicTag[] {
  const seen = new Set<TopicTag>();
  for (const p of personas) {
    if (!p.topicTags) continue;
    for (const t of p.topicTags) seen.add(t);
  }
  return TOPIC_TAGS.filter((t) => seen.has(t));
}

function matchesFilters(persona: Persona, filters: BrowseFilterState): boolean {
  if (filters.hsk.size > 0) {
    if (!persona.hskLevel || !filters.hsk.has(persona.hskLevel)) return false;
  }
  if (filters.prof.size > 0) {
    if (!persona.professionTag || !filters.prof.has(persona.professionTag)) return false;
  }
  if (filters.topic.size > 0) {
    const hit = persona.topicTags?.some((t) => filters.topic.has(t));
    if (!hit) return false;
  }
  return true;
}

export function BrowseView({ personas, filters, onFiltersChange, onOpenProfile }: BrowseViewProps) {
  const profs = useMemo(() => uniqueProfs(personas), [personas]);
  const topics = useMemo(() => usedTopicTags(personas), [personas]);

  const visible = useMemo(
    () => personas.filter((p) => matchesFilters(p, filters)),
    [personas, filters],
  );

  const anyActive = filters.hsk.size > 0 || filters.prof.size > 0 || filters.topic.size > 0;

  function clearAll() {
    onFiltersChange(EMPTY_BROWSE_FILTERS);
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div
        className="px-4 pt-2 pb-3 space-y-2.5 flex-shrink-0"
        style={{ borderBottom: `1px solid ${C.blush}` }}
      >
        <FilterGroup label="HSK" hz="级别">
          {HSK_LEVELS.map((lvl) => (
            <FilterChip
              key={lvl}
              label={`HSK ${lvl}`}
              selected={filters.hsk.has(lvl)}
              onToggle={() => onFiltersChange({ ...filters, hsk: toggle(filters.hsk, lvl) })}
            />
          ))}
        </FilterGroup>

        {/* Profession and Topic each get their own single horizontally-
            scrollable row — keeps the persona list tall without forcing the
            two categories to share visual space. */}
        <ScrollFilterGroup label="Profession" hz="职业">
          {profs.map((tag) => (
            <span key={`prof-${tag}`} className="flex-shrink-0">
              <FilterChip
                label={tag}
                selected={filters.prof.has(tag)}
                onToggle={() => onFiltersChange({ ...filters, prof: toggle(filters.prof, tag) })}
              />
            </span>
          ))}
        </ScrollFilterGroup>

        <ScrollFilterGroup label="Topic" hz="话题">
          {topics.map((t) => (
            <span key={`topic-${t}`} className="flex-shrink-0">
              <FilterChip
                label={t}
                selected={filters.topic.has(t)}
                onToggle={() => onFiltersChange({ ...filters, topic: toggle(filters.topic, t) })}
              />
            </span>
          ))}
        </ScrollFilterGroup>

        {anyActive && (
          <div className="pt-1">
            <button
              type="button"
              onClick={clearAll}
              className="ff-body text-[11px] underline"
              style={{ color: C.vermillion }}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {visible.length === 0 ? (
          <div
            className="rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 gap-3"
            style={{ background: C.paper, color: C.inkLight }}
          >
            <div className="ff-zh-serif text-5xl opacity-50" style={{ color: C.gold }} aria-hidden>
              空
            </div>
            <div>
              <div className="ff-display italic text-xl" style={{ color: C.ink }}>
                No one matches
              </div>
              <div className="ff-zh-serif text-sm mt-0.5" style={{ color: C.inkLight }}>
                没有匹配的人
              </div>
            </div>
            <button
              type="button"
              onClick={clearAll}
              className="ff-body text-[12px] px-4 py-2 rounded-full font-semibold"
              style={{ background: C.vermillion, color: 'white' }}
            >
              Clear filters
            </button>
          </div>
        ) : (
          <ul className="space-y-2 anim-slide-up">
            {visible.map((p) => (
              <BrowsePersonaRow key={p.id} persona={p} onOpen={() => onOpenProfile(p)} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Labeled group whose chips live on a single horizontally-scrollable row —
// same heading treatment as FilterGroup but the chips don't wrap. The row
// bleeds to the screen edge (`-mx-4 px-4`) so the rightmost chip clips
// cleanly instead of vanishing under section padding.
function ScrollFilterGroup({
  label,
  hz,
  children,
}: {
  label: string;
  hz: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        className="ff-body text-[9px] uppercase tracking-[0.28em] mb-1.5 flex items-baseline gap-2"
        style={{ color: C.inkSofter }}
      >
        {label}
        <span className="ff-zh-serif text-[11px] tracking-normal opacity-80">· {hz}</span>
      </div>
      <div
        className="flex items-center gap-1.5 overflow-x-auto no-scrollbar -mx-4 px-4"
        style={{ scrollbarWidth: 'none' }}
        aria-label={`${label} filters`}
      >
        {children}
      </div>
    </div>
  );
}

function FilterGroup({
  label,
  hz,
  children,
}: {
  label: string;
  hz: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        className="ff-body text-[9px] uppercase tracking-[0.28em] mb-1.5 flex items-baseline gap-2"
        style={{ color: C.inkSofter }}
      >
        {label}
        <span className="ff-zh-serif text-[11px] tracking-normal opacity-80">· {hz}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function BrowsePersonaRow({ persona, onOpen }: { persona: Persona; onOpen: () => void }) {
  return (
    <li
      className="relative rounded-2xl overflow-hidden shadow-paper"
      style={{ background: C.cream, border: `1px solid ${C.mist}` }}
    >
      <button
        type="button"
        onClick={onOpen}
        className="w-full flex items-center text-left"
        aria-label={`View ${persona.name}'s profile`}
      >
        <span className="block p-3 flex-shrink-0">
          <span className="block w-12 h-12 rounded-full overflow-hidden" aria-hidden>
            <Portrait persona={persona} />
          </span>
        </span>
        <span className="flex-1 min-w-0 py-3 pr-3">
          <span className="flex items-baseline gap-2">
            <span className="ff-zh-serif text-lg font-bold leading-none truncate">
              {persona.name}
            </span>
            <span
              className="ff-body text-[11px] opacity-70 truncate"
              style={{ color: C.inkLight }}
            >
              {persona.pinyin}
            </span>
          </span>
          <span
            className="mt-1.5 flex items-center gap-1.5 ff-body text-[11px]"
            style={{ color: C.inkLight }}
          >
            <span
              className="px-1.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider"
              style={{ background: C.blush, color: C.vermillionDeep }}
            >
              {persona.level.tag}
            </span>
            <span className="truncate">
              {persona.cityEn} · {persona.level.vibe}
            </span>
          </span>
        </span>
      </button>
    </li>
  );
}
